import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Plus,
  Copy,
  Pencil,
  Trash2,
  Archive,
  ExternalLink,
  CalendarClock,
  Search,
  X as XIcon,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useWorkspace } from "@/contexts/WorkspaceContext";
import { useAuth } from "@/contexts/AuthContext";
import { COMMON_TIMEZONES, userBrowserTimezone, formatInTz } from "@/lib/time";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";

const WEEKDAYS = [
  { v: 1, l: "Mon" },
  { v: 2, l: "Tue" },
  { v: 3, l: "Wed" },
  { v: 4, l: "Thu" },
  { v: 5, l: "Fri" },
  { v: 6, l: "Sat" },
  { v: 0, l: "Sun" },
];

export default function Bookings() {
  const { currentWorkspace } = useWorkspace();
  const { user } = useAuth();
  const [links, setLinks] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [editLink, setEditLink] = useState(null);
  const [search, setSearch] = useState("");

  const load = async () => {
    if (!currentWorkspace) return;
    const [l, s] = await Promise.all([
      api.get(`/bookings/links`, { params: { workspace_id: currentWorkspace.workspace_id } }),
      api.get(`/bookings/submissions`, { params: { workspace_id: currentWorkspace.workspace_id } }),
    ]);
    setLinks(l.data);
    setSubmissions(s.data);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [currentWorkspace]);

  const copyLink = (slug) => {
    navigator.clipboard.writeText(`${window.location.origin}/book/${slug}`);
    toast.success("Public booking link copied");
  };
  const archive = async (link) => {
    await api.patch(`/bookings/links/${link.link_id}`, { is_archived: true });
    toast.success("Booking link archived");
    load();
  };
  const remove = async (link) => {
    if (!window.confirm(`Delete "${link.title}"?`)) return;
    await api.delete(`/bookings/links/${link.link_id}`);
    toast.success("Booking link deleted");
    load();
  };

  // Per-page search: filters BOTH the links + submissions tabs.
  const filteredLinks = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return links;
    return links.filter((l) => {
      const fields = [l.title, l.description, l.public_slug, l.timezone];
      return fields.some((f) => String(f || "").toLowerCase().includes(q));
    });
  }, [links, search]);
  const filteredSubs = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return submissions;
    return submissions.filter((s) => {
      const fields = [s.name, s.email, s.notes, s.status];
      return fields.some((f) => String(f || "").toLowerCase().includes(q));
    });
  }, [submissions, search]);

  return (
    <div className="flex flex-col gap-7" data-testid="bookings-page">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="bloq-eyebrow">Bookings</p>
          <h1 className="bloq-headline text-4xl sm:text-5xl mt-2">
            Let clients book themselves in.
          </h1>
        </div>
        <button onClick={() => { setEditLink(null); setShowCreate(true); }} className="bloq-btn-primary inline-flex items-center gap-2" data-testid="new-booking-link-button">
          <Plus className="w-4 h-4" /> New booking link
        </button>
      </header>

      <Tabs defaultValue="links">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <TabsList>
            <TabsTrigger value="links" data-testid="tab-links">Links</TabsTrigger>
            <TabsTrigger value="submissions" data-testid="tab-submissions">Submissions ({submissions.length})</TabsTrigger>
          </TabsList>
          <div className="relative flex-1 min-w-[220px] max-w-md">
            <Search className="w-4 h-4 text-[#82828c] absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search bookings: title, invitee, email, notes…"
              className="bloq-input pl-9 pr-9 w-full"
              data-testid="bookings-search-input"
            />
            {search && (
              <button
                onClick={() => setSearch("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-[#f5efe6]"
                data-testid="bookings-search-clear"
                aria-label="Clear search"
              >
                <XIcon className="w-3.5 h-3.5 text-[#82828c]" />
              </button>
            )}
          </div>
        </div>
        <TabsContent value="links" className="mt-5">
          {filteredLinks.length === 0 ? (
            <div className="bloq-card p-10 text-center">
              <CalendarClock className="w-10 h-10 mx-auto text-[#82828c] mb-3" />
              <p className="bloq-headline text-xl mb-1 text-[#005e7a]">{search ? "No matching booking links" : "No booking links yet"}</p>
              <p className="text-sm text-[#82828c] mb-5">
                {search
                  ? `No booking links match "${search}".`
                  : "Set up your availability, share the link, watch the bookings roll in."}
              </p>
              {!search && <button onClick={() => setShowCreate(true)} className="bloq-btn-primary inline-flex items-center gap-2"><Plus className="w-4 h-4" /> Create one</button>}
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4">
              {filteredLinks.map((l) => (
                <div key={l.link_id} className="bloq-card p-5 flex flex-col gap-3" data-testid={`booking-link-${l.link_id}`}>
                  <div>
                    <p className="bloq-headline text-lg text-[#005e7a]">{l.title}</p>
                    <p className="text-sm text-[#82828c] mt-1">{l.duration_minutes}m · {l.timezone}</p>
                  </div>
                  <code className="text-xs bg-[#f5efe6] p-2.5 rounded-md break-all">{`${window.location.origin}/book/${l.public_slug}`}</code>
                  <div className="flex flex-wrap gap-2">
                    <button onClick={() => copyLink(l.public_slug)} className="bloq-btn-secondary text-xs px-3 py-1.5 inline-flex items-center gap-1" data-testid={`copy-booking-${l.link_id}`}><Copy className="w-3 h-3" /> Copy</button>
                    <Link to={`/book/${l.public_slug}`} target="_blank" className="bloq-btn-secondary text-xs px-3 py-1.5 inline-flex items-center gap-1" data-testid={`preview-booking-${l.link_id}`}><ExternalLink className="w-3 h-3" /> Preview</Link>
                    <button onClick={() => { setEditLink(l); setShowCreate(true); }} className="bloq-btn-secondary text-xs px-3 py-1.5 inline-flex items-center gap-1" data-testid={`edit-booking-${l.link_id}`}><Pencil className="w-3 h-3" /> Edit</button>
                    <button onClick={() => archive(l)} className="bloq-btn-secondary text-xs px-3 py-1.5 inline-flex items-center gap-1" data-testid={`archive-booking-${l.link_id}`}><Archive className="w-3 h-3" /></button>
                    <button onClick={() => remove(l)} className="bloq-btn-secondary text-xs px-3 py-1.5 inline-flex items-center gap-1 text-[#c43838]" data-testid={`delete-booking-${l.link_id}`}><Trash2 className="w-3 h-3" /></button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
        <TabsContent value="submissions" className="mt-5">
          {filteredSubs.length === 0 ? (
            <div className="bloq-card p-10 text-center">
              <p className="text-sm text-[#82828c]">
                {search
                  ? `No submissions match "${search}".`
                  : "When someone books you, the meeting + their contact info will show up here."}
              </p>
            </div>
          ) : (
            <div className="bloq-card p-2 overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-widest text-[#82828c]">
                    <th className="px-3 py-3">Name</th>
                    <th className="px-3 py-3">Email</th>
                    <th className="px-3 py-3">When</th>
                    <th className="px-3 py-3">Notes</th>
                    <th className="px-3 py-3">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredSubs.map((s) => (
                    <tr key={s.submission_id} className="hover:bg-[#f5efe6]" data-testid={`submission-${s.submission_id}`}>
                      <td className="px-3 py-3 font-medium">{s.name}</td>
                      <td className="px-3 py-3">{s.email}</td>
                      <td className="px-3 py-3">{formatInTz(s.selected_time, user?.timezone || userBrowserTimezone())}</td>
                      <td className="px-3 py-3 max-w-xs truncate" title={s.notes}>{s.notes || "—"}</td>
                      <td className="px-3 py-3"><span className="bloq-pill">{s.status}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>
      </Tabs>

      <BookingLinkDialog
        open={showCreate}
        onOpenChange={(o) => { setShowCreate(o); if (!o) setEditLink(null); }}
        link={editLink}
        onSaved={() => { load(); setShowCreate(false); setEditLink(null); }}
      />
    </div>
  );
}

function BookingLinkDialog({ open, onOpenChange, link, onSaved }) {
  const { currentWorkspace } = useWorkspace();
  const { user } = useAuth();
  const [title, setTitle] = useState("");
  const [desc, setDesc] = useState("");
  const [duration, setDuration] = useState(30);
  const [tz, setTz] = useState(user?.timezone || userBrowserTimezone());
  const [weekdays, setWeekdays] = useState([1, 2, 3, 4, 5]);
  const [startHour, setStartHour] = useState(9);
  const [endHour, setEndHour] = useState(17);
  const [active, setActive] = useState(true);

  useEffect(() => {
    if (link) {
      setTitle(link.title || "");
      setDesc(link.description || "");
      setDuration(link.duration_minutes || 30);
      setTz(link.timezone || "UTC");
      setWeekdays(link.availability?.weekdays || [1, 2, 3, 4, 5]);
      setStartHour(link.availability?.start_hour ?? 9);
      setEndHour(link.availability?.end_hour ?? 17);
      setActive(link.active !== false);
    } else {
      setTitle("");
      setDesc("");
      setDuration(30);
      setTz(user?.timezone || userBrowserTimezone());
      setWeekdays([1, 2, 3, 4, 5]);
      setStartHour(9);
      setEndHour(17);
      setActive(true);
    }
  }, [link, open, user]);

  const toggleDay = (d) => {
    setWeekdays((wd) => (wd.includes(d) ? wd.filter((x) => x !== d) : [...wd, d]));
  };

  const submit = async () => {
    if (!title.trim()) { toast.error("Give your link a title"); return; }
    if (!currentWorkspace) return;
    const payload = {
      title: title.trim(),
      description: desc.trim(),
      duration_minutes: Number(duration),
      timezone: tz,
      availability: { weekdays, start_hour: Number(startHour), end_hour: Number(endHour) },
      workspace_id: currentWorkspace.workspace_id,
    };
    try {
      if (link) {
        await api.patch(`/bookings/links/${link.link_id}`, { ...payload, active });
      } else {
        await api.post(`/bookings/links`, payload);
      }
      toast.success(link ? "Booking link updated" : "Booking link created");
      onSaved();
    } catch { toast.error("Could not save"); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>{link ? "Edit booking link" : "New booking link"}</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-4 mt-2">
          <Field label="Title">
            <input value={title} onChange={(e) => setTitle(e.target.value)} className="bloq-input" placeholder="30-min intro call" data-testid="bl-title-input" />
          </Field>
          <Field label="Description">
            <textarea rows={2} value={desc} onChange={(e) => setDesc(e.target.value)} className="bloq-input" placeholder="What this meeting is for…" data-testid="bl-description-input" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Duration">
              <Select value={String(duration)} onValueChange={(v) => setDuration(Number(v))}>
                <SelectTrigger data-testid="bl-duration-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {[15, 30, 45, 60, 90].map((d) => <SelectItem key={d} value={String(d)}>{d} min</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Timezone">
              <Select value={tz} onValueChange={setTz}>
                <SelectTrigger data-testid="bl-tz-select"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-60">
                  {COMMON_TIMEZONES.map((z) => <SelectItem key={z} value={z}>{z}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
          </div>
          <Field label="Available days">
            <div className="flex flex-wrap gap-2 mt-1">
              {WEEKDAYS.map((d) => {
                const on = weekdays.includes(d.v);
                return (
                  <button
                    key={d.v}
                    onClick={() => toggleDay(d.v)}
                    className="px-3 py-1.5 rounded-md text-xs font-medium transition-colors"
                    style={{ background: on ? "#005e7a" : "#f5efe6", color: on ? "#fff" : "#1a1a1f" }}
                    data-testid={`bl-day-${d.v}`}
                  >
                    {d.l}
                  </button>
                );
              })}
            </div>
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Start hour (24h)">
              <input type="number" min={0} max={23} value={startHour} onChange={(e) => setStartHour(e.target.value)} className="bloq-input" data-testid="bl-start-hour" />
            </Field>
            <Field label="End hour (24h)">
              <input type="number" min={0} max={23} value={endHour} onChange={(e) => setEndHour(e.target.value)} className="bloq-input" data-testid="bl-end-hour" />
            </Field>
          </div>
          {link && (
            <label className="flex items-center justify-between p-3 rounded-lg bg-[#f5efe6]">
              <span className="text-sm">Active</span>
              <Switch checked={active} onCheckedChange={setActive} data-testid="bl-active-toggle" />
            </label>
          )}
          <button onClick={submit} className="bloq-btn-primary" data-testid="bl-save-button">
            {link ? "Save changes" : "Create booking link"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-widest text-[#82828c] font-medium block mb-1.5">{label}</label>
      {children}
    </div>
  );
}
